import React, { FC, useMemo } from 'react';
import { Box } from '~Styles';
import { useI18n, useSelector } from '~Hooks';
import { CompleteBongQuery_venue_company_categories } from '~Scenes/POS/Queries/__queries__';
import { useInventoryApi, useInventoryHelpers } from '~Components/InventoryUniverse';
import CategoryRow from './CategoryRow';
import useConfirm from '~Hooks/useConfirm';

interface Props {
  categories: CompleteBongQuery_venue_company_categories[];
  search?: string;
}

const Categories: FC<Props> = ({ categories, search = '' }) => {
  const { i18n } = useI18n();
  const venueId = useSelector((state) => state.venueId);
  const inventoryApi = useInventoryApi(venueId);
  const { confirm } = useConfirm();
  const { itemIsOutOfStock } = useInventoryHelpers();

  // Filter and sort categories alphabetically by translated name
  const sortedCategories = useMemo(() => {
    const query = search.toLowerCase();
    return [...categories]
      .filter((cat) => !query || i18n(cat.nameLang).toLowerCase().includes(query))
      .sort((a, b) => {
        const nameA = i18n(a.nameLang).toLowerCase();
        const nameB = i18n(b.nameLang).toLowerCase();
        return nameA.localeCompare(nameB);
      });
  }, [categories, i18n, search]);

  return (
    <Box>
      {sortedCategories.map((category) => {
        const isOutOfStock = itemIsOutOfStock(category.id);

        return (
          <CategoryRow
            key={category.id}
            category={category}
            isOutOfStock={isOutOfStock}
            onToggleStock={async () => {
              const confirmed = await confirm(
                isOutOfStock
                  ? i18n('Inventory.SetCategoryInStock')
                  : i18n('Inventory.SetCategoryOutOfStock'),
              );
              if (!confirmed) return;
              inventoryApi.setOutOfStock(category.id, !isOutOfStock);
            }}
            i18n={i18n}
          />
        );
      })}
    </Box>
  );
};

export default Categories;
